// @greenlight-spec: 28-it-dashboard-ui.md

import * as React from 'react';
import { Check, Copy } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { copyTextToClipboard } from '@/lib/clipboard';
import { cn } from '@/lib/utils';

export const FIRST_APP_COMMAND = '/greenlight';

/** Realistic first apps, each small enough for one prompt and each showing what Greenlight governs. */
export const FIRST_APP_IDEAS: readonly { title: string; ask: string }[] = [
  {
    title: 'PTO requests',
    ask: 'Make me a PTO request app where managers approve requests and everyone can see who is out',
  },
  {
    title: 'New-hire onboarding',
    ask: 'Make me an onboarding checklist app for new hires, with tasks their manager and IT tick off',
  },
  {
    title: 'Expense receipts',
    ask: 'Make me an expense app where people upload receipts and finance approves them',
  },
  {
    title: 'Room booking',
    ask: 'Make me a meeting-room booking app that shows what is free right now',
  },
  {
    title: 'Friday lunch',
    ask: 'Make me a lunch poll app where the team votes on where to eat on Friday',
  },
  {
    title: 'Shout-outs',
    ask: 'Make me a shout-out board where people thank a teammate and the week’s top three are pinned',
  },
];

function IdeaCard({ title, ask }: { title: string; ask: string }): React.JSX.Element {
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<number | null>(null);
  React.useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );
  const command = `${FIRST_APP_COMMAND} ${ask}`;

  return (
    <button
      type="button"
      onClick={async () => {
        if (!(await copyTextToClipboard(command))) return;
        setCopied(true);
        if (timer.current !== null) window.clearTimeout(timer.current);
        timer.current = window.setTimeout(() => setCopied(false), 1500);
      }}
      aria-label={copied ? `Copied: ${command}` : `Copy: ${command}`}
      className={cn(
        'group flex min-h-11 flex-col gap-1 rounded-lg border border-border bg-background p-3 text-left text-sm',
        'transition-colors hover:border-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      )}
      data-testid="first-app-idea"
    >
      <span className="flex items-center justify-between gap-2">
        <span className="font-medium text-foreground">{title}</span>
        <span className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
          {copied ? 'Copied' : 'Copy'}
          {copied ? (
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
          ) : (
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          )}
        </span>
      </span>
      <span className="font-mono text-xs leading-relaxed text-muted-foreground">
        <span className="font-semibold text-gl-green-text">{FIRST_APP_COMMAND}</span> {ask}
      </span>
    </button>
  );
}

/** The one thing to do once Greenlight is configured: ask a coding agent for an app. */
export function FirstAppPrompt({ className }: { className?: string }): React.JSX.Element {
  return (
    <div
      className={cn('space-y-3 text-sm text-muted-foreground', className)}
      data-testid="first-app-prompt"
    >
      <p>
        Make your first app with your coding agent. Open it and type{' '}
        <span className="font-mono font-semibold text-gl-green-text">{FIRST_APP_COMMAND}</span>{' '}
        followed by what you want, or pick one of these to copy:
      </p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="first-app-ideas">
        {FIRST_APP_IDEAS.map((idea) => (
          <IdeaCard key={idea.title} {...idea} />
        ))}
      </div>
      <p>
        Your agent builds and deploys the app through Greenlight, and it shows up under Apps. Apps
        that need company data, such as a payroll system or a document library, reach it through the
        integrations IT sets up.
      </p>
    </div>
  );
}

/** Settings → Integrations, offered beside the first-app prompt. */
export function SetUpIntegrationsButton({
  variant = 'outline',
}: {
  variant?: 'outline' | 'ghost';
}): React.JSX.Element {
  return (
    <Button asChild variant={variant} size="sm" className="h-11 md:h-9">
      <Link to="/integrations" data-testid="set-up-integrations">
        Set up integrations
      </Link>
    </Button>
  );
}
